import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme, usePageTitle, AlertBanner } from '@flowcore-water/ui';
import { fetchEntities, type EntitySummary } from '../lib/api';

export function EntityIndex() {
  const [entities, setEntities] = useState<EntitySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTheme();
  usePageTitle('ST Mirror', 'Home');

  useEffect(() => {
    fetchEntities()
      .then((r) => setEntities(r.entities))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingMessage />;
  if (error) return <ErrorMessage message={error} />;

  const totalRecords = entities.reduce((sum, e) => sum + e.count, 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: t.textPrimary }}>
          ServiceTitan Mirror
        </h1>
        <p className="mt-1 text-sm" style={{ color: t.textMuted }}>
          {totalRecords.toLocaleString()} records across {entities.length} entities
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {entities.map((entity) => (
          <Link
            key={entity.key}
            to={`/browse/${entity.key}`}
            className="group rounded-xl border p-5 transition-colors"
            style={{
              background: t.cardBg,
              borderColor: t.borderSubtle,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = t.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = t.borderSubtle;
            }}
          >
            <div className="flex items-start justify-between">
              <h2 className="font-semibold" style={{ color: t.textPrimary }}>
                {entity.display}
              </h2>
              <span
                className="rounded-full px-2.5 py-0.5 text-xs font-mono"
                style={{ background: t.accentBg, color: t.accent }}
              >
                {entity.count.toLocaleString()}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: t.textMuted }}>
              <SyncIndicator status={entity.last_sync_status} />
              <span>
                {entity.last_sync_at
                  ? `Synced ${formatRelativeTime(entity.last_sync_at)}`
                  : 'Never synced'}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function SyncIndicator({ status }: { status: string | null }) {
  const { t } = useTheme();
  const color = status === 'ok' ? t.success : status === 'error' ? t.fail : t.textMuted;

  return (
    <span
      className="inline-block h-2 w-2 rounded-full"
      style={{ background: color }}
    />
  );
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function LoadingMessage() {
  const { t } = useTheme();
  return (
    <div className="flex h-64 items-center justify-center">
      <span className="text-sm" style={{ color: t.textMuted }}>Loading...</span>
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return <AlertBanner variant="error" message={message} />;
}
